import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CasesBackendService } from '@dev-fast/backend-services';
import type { ICaseItemDtoV2, ICaseOpenPrizeDataDto, ICaseRevisionItem, ICaseSpinItem, IUserDetailed } from '@dev-fast/types';
import { CasesGamePhases, EAgentKey, IItemStatus } from '@dev-fast/types';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import { Action, State } from '@ngxs/store';
import { compose, insertItem, patch, removeItem } from '@ngxs/store/operators';
import type { Observable } from 'rxjs';
import { delay, filter, forkJoin, of, switchMap, throwError } from 'rxjs';
import { catchError, debounceTime, tap } from 'rxjs/operators';

import { Login, LogoutSuccess } from '@app/auth';
import { UserService } from '@app/core/user-service';

import { eventStateAdapter, getCurrentLevelProgressPercent, levelQuestUserLevelUpdateAdapter } from '../adapters/event-state.adapter';
import { motivatorAdapter, motivatorItemAdapter } from '../adapters/motivator.adapter';
import { possibleItemsAdapter } from '../adapters/possible-items.adapter';
import { FINAL_LEVEL_ID } from '../event.constants';
import { AprilMadnessEventBackendService } from '../services/april-madness-event-backend.service';
import { AprilMadnessEventModalsService } from '../services/april-madness-event-modals.service';
import type {
  ICurrentLevelQuest,
  ILastDropData,
  ILevelQuestUserInfo,
  ILevelQuestUserLevelUpdate,
  IQuestMotivator,
  ITakeReward,
} from '../types/backend';
import type { ILevelQuestUserUpdate } from '../types/backend/level-quest-user-update.interface';
import { EAprilMadnessEventLevelStatus } from '../types/frontend/april-madness-event-level-status.enum';
import {
  CollectLevelReward,
  GetAprilMadnessEventState,
  GetMotivator,
  GetPossibleItems,
  Replay,
  TakeReward,
} from './april-madness-event.actions';
import type { AprilMadnessEventModel } from './april-madness-event.model';
import { DEFAULT_APRIL_MADNESS_EVENT_STATE } from './april-madness-event.model';

@State<AprilMadnessEventModel>({
  name: 'aprilMadnessEvent',
  defaults: DEFAULT_APRIL_MADNESS_EVENT_STATE,
})
@Injectable()
export class AprilMadnessEventState implements NgxsOnInit {
  #aprilMadnessEventBackendService = inject(AprilMadnessEventBackendService);
  #aprilMadnessEventModalsService = inject(AprilMadnessEventModalsService);
  #casesBackendService = inject(CasesBackendService);
  #userService = inject(UserService);

  user = toSignal<IUserDetailed | null | undefined>(this.#userService.user$);

  ngxsOnInit(context: StateContext<AprilMadnessEventModel>): void {
    const { dispatch } = context;
    dispatch([new GetAprilMadnessEventState()]);
    this.#aprilMadnessEventModalsService.registerModals();

    /**
     * РЎРѕР±С‹С‚РёРµ РјРѕС‚РёРІР°С‚РѕСЂР° "РўРѕРї РёРіСЂРѕРєРѕРІ"
     * */
    this.#aprilMadnessEventBackendService
      .lastWonEvent()
      .pipe(
        filter((motivator) => motivator.agentKey === EAgentKey.LEADERS_RACE),
        tap((motivatorData) => this.#onLastWonEvent(context, motivatorData.data)),
      )
      .subscribe();

    /**
     * РЎРѕР±С‹С‚РёРµ РѕР±РЅРѕРІР»РµРЅРёСЏ РѕРїС‹С‚Р°
     * */
    this.#aprilMadnessEventBackendService
      .levelQuestUserUpdateEvent()
      .pipe(
        filter((userUpdate) => userUpdate.userId === this.user()?.id),
        tap((userUpdate) => this.#onLevelQuestUserUpdate(context, userUpdate)),
      )
      .subscribe();

    /**
     * РЎРѕР±С‹С‚РёРµ РѕР±РЅРѕРІР»РµРЅРёСЏ СѓСЂРѕРІРЅСЏ
     * */
    this.#aprilMadnessEventBackendService
      .levelQuestUserLevelUpdateEvent()
      .pipe(
        delay(300),
        tap((userUpdate) => this.#onLevelQuestUserLevelUpdate(context, userUpdate)),
      )
      .subscribe();
  }

  @Action(GetAprilMadnessEventState)
  getEventState(
    { patchState, dispatch, getState }: StateContext<AprilMadnessEventModel>,
    { loadAnyway }: GetAprilMadnessEventState,
  ): Observable<[ICurrentLevelQuest | null, ILevelQuestUserInfo | null] | null> {
    const { initialized } = getState();

    if (initialized && !loadAnyway) {
      return of(null);
    }

    patchState({ isEventStateLoading: true });

    return this.#aprilMadnessEventBackendService.getCurrentQuest().pipe(
      switchMap((quest: ICurrentLevelQuest | null) => {
        if (!quest?.id) {
          return of([quest, null] as [ICurrentLevelQuest | null, ILevelQuestUserInfo | null]);
        }

        return forkJoin([of(quest), this.#getUserQuestInfo(quest.id)]);
      }),
      tap(([currentLevelQuest, userQuestInfo]: [ICurrentLevelQuest | null, ILevelQuestUserInfo | null]) => {
        patchState({ ...eventStateAdapter(currentLevelQuest, userQuestInfo) });
        patchState({ initialized: true, isEventStateLoading: false });
        dispatch([new GetPossibleItems()]);
      }),
      catchError((error) => {
        patchState({ isEventStateLoading: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(GetPossibleItems)
  getPossibleItems({
    getState,
    patchState,
  }: StateContext<AprilMadnessEventModel>): Observable<[ICaseItemDtoV2, ICaseItemDtoV2 | null] | null> {
    const { currentLevelQuestLevel, eventLevels } = getState();

    if (!currentLevelQuestLevel?.caseId) {
      return of(null);
    }

    const finalLevel = eventLevels.find((level) => level.levelId === FINAL_LEVEL_ID);
    const finalCaseId = finalLevel?.caseId;
    const currentCaseId = currentLevelQuestLevel.caseId;
    const shouldFetchFinalCase = finalCaseId && finalCaseId !== currentCaseId;

    return forkJoin([
      this.#casesBackendService.getCaseItemById(currentCaseId),
      shouldFetchFinalCase ? this.#casesBackendService.getCaseItemById(finalCaseId) : of(null),
    ]).pipe(
      tap(([currentCaseItem, finalCaseItem]) => {
        const currentItems = possibleItemsAdapter(currentCaseItem);

        if (finalCaseItem) {
          const finalItems = possibleItemsAdapter(finalCaseItem);
          const mostExpensiveFinalItem = finalItems.filter((item) => item.image).sort((a, b) => b.price - a.price)[0];

          if (mostExpensiveFinalItem && currentItems.length > 0) {
            const [first, ...rest] = currentItems;
            patchState({ possibleItems: [first, mostExpensiveFinalItem, ...rest] });
            return;
          }
        }

        patchState({ possibleItems: currentItems });
      }),
    );
  }

  @Action(TakeReward)
  takeReward(stateContext: StateContext<AprilMadnessEventModel>, { levelQuestLevelId }: TakeReward): Observable<ITakeReward | null> {
    const { getState } = stateContext;
    const { currentLevelQuestLevel, eventLevels } = getState();
    const caseId = currentLevelQuestLevel?.caseId || 0;

    const level = eventLevels.find((eventLevel) => eventLevel.levelId === levelQuestLevelId);
    const step = level?.levelId || 1;
    const levelBackendId = level?.levelBackendId || 0;

    return this.#getCaseItemByIdAndSetCaseItems(stateContext, caseId).pipe(
      switchMap(() => {
        this.#aprilMadnessEventModalsService.openOpenCaseModal({ step, levelBackendId });
        return this.#aprilMadnessEventBackendService.collectLevelReward(levelBackendId);
      }),
      catchError(() => {
        return of(null);
      }),
    );
  }

  @Action(GetMotivator)
  getMotivator({ patchState }: StateContext<AprilMadnessEventModel>): Observable<IQuestMotivator> {
    return this.#aprilMadnessEventBackendService.getLevelQuestMotivator().pipe(
      tap((motivator) => {
        patchState({ topOfGamers: motivatorAdapter(motivator) });
      }),
    );
  }

  @Action(CollectLevelReward)
  collectLevelReward(_: StateContext<AprilMadnessEventModel>, { levelBackendId }: CollectLevelReward): Observable<ITakeReward | null> {
    return this.#aprilMadnessEventBackendService.collectLevelReward(levelBackendId).pipe(catchError(() => of(null)));
  }

  @Action(Replay)
  replay(stateContext: StateContext<AprilMadnessEventModel>, { openUUID, levelQuestLevelId }: Replay): Observable<ICaseOpenPrizeDataDto> {
    const { getState, patchState } = stateContext;
    const { eventLevels } = getState();
    const caseId = eventLevels.find((l) => l.levelId === levelQuestLevelId)?.caseId || 0;

    patchState({ spinState: CasesGamePhases.REQUEST, prizeSpinTracks: [], caseItems: {} });
    this.#aprilMadnessEventModalsService.openOpenCaseModal({ step: levelQuestLevelId, isReplay: true });

    return this.#getCaseItemByIdAndSetCaseItems(stateContext, caseId).pipe(
      switchMap((caseItem: ICaseItemDtoV2) => {
        return this.#casesBackendService.getReplays(openUUID).pipe(
          tap((replayData: ICaseOpenPrizeDataDto) => {
            this.#setPrizeSpinTracksForReplay(stateContext, replayData);
          }),
        );
      }),
      debounceTime(300),
      tap(() => {
        patchState({ spinState: CasesGamePhases.RAFFLE });
      }),
    );
  }

  @Action(Login)
  onLogin({ dispatch }: StateContext<AprilMadnessEventModel>): void {
    dispatch([new GetAprilMadnessEventState(true)]);
  }

  @Action(LogoutSuccess)
  onLogout({ dispatch, patchState }: StateContext<AprilMadnessEventModel>): void {
    patchState({
      currentStep: 0,
      currentExp: 0,
      currentLevelQuestLevel: null,
      userTakenItems: [],
      collected: 0,
    });
    dispatch([new GetAprilMadnessEventState(true)]);
  }

  /* HELPERS */

  #updatePrizeSpinTracks({ getState, patchState }: StateContext<AprilMadnessEventModel>, results: ICaseItemDtoV2[]): void {
    const { prizeSpinTracks } = getState();
    const updatedTracks: ICaseSpinItem[][] = [];

    prizeSpinTracks.forEach((track, index) => {
      const currentPrizeItem = track[28];
      const rewardInventoryItem = results[index]?.userInventoryItem?.inventoryItem;

      if (!currentPrizeItem) {
        updatedTracks.push(track);
        return;
      }

      const inventoryItem = rewardInventoryItem
        ? {
            available:
              rewardInventoryItem.available === undefined
                ? currentPrizeItem.inventoryItem.available
                : rewardInventoryItem.available === 'true',
            baseItem: rewardInventoryItem.baseItem,
            price: rewardInventoryItem.price,
            id: rewardInventoryItem.id,
            steamPrice: rewardInventoryItem.steamPrice,
          }
        : currentPrizeItem.inventoryItem;

      const prizeItem: ICaseSpinItem = {
        ...currentPrizeItem,
        id: results[index]?.case?.caseRevision?.id || currentPrizeItem.id,
        count: 1,
        inventoryItem,
        status: IItemStatus.TAKEN,
        trackIndex: index,
        userInventoryItemId: results[index]?.userInventoryItem?.id || 0,
      };

      const updatedTrack = [...track.slice(0, 28), prizeItem, ...track.slice(29)];
      updatedTracks.push(updatedTrack);
    });
    patchState({ prizeSpinTracks: updatedTracks });
  }

  #getCaseItemByIdAndSetCaseItems({ patchState }: StateContext<AprilMadnessEventModel>, caseId: number): Observable<ICaseItemDtoV2> {
    return this.#casesBackendService.getCaseItemById(caseId).pipe(
      tap((caseItem) => {
        patchState({
          caseItems: this.#spinItemsMapper(caseItem?.lastRevision.items || []),
        });
      }),
    );
  }

  #setPrizeSpinTracks(
    { patchState, getState }: StateContext<AprilMadnessEventModel>,
    caseItem: ICaseItemDtoV2,
    spinItems: number[][],
  ): void {
    const { caseItems, prizeSpinTracks } = getState();
    const innerConstructor = (ids: number[][]): ICaseSpinItem[][] => {
      const newSpinTracks: ICaseSpinItem[][] = [];
      ids.forEach((line, index) => {
        const lines: ICaseSpinItem[] = [];
        line.forEach((id) => {
          if (caseItems[id] !== undefined) {
            lines.push({ ...caseItems[id], trackIndex: index });
          }
        });
        if (prizeSpinTracks[index]?.length) {
          newSpinTracks.push([...prizeSpinTracks[index], ...lines.slice(7)]);
        } else {
          newSpinTracks.push(lines);
        }
      });
      return newSpinTracks;
    };
    patchState({
      prizeSpinTracks: spinItems ? innerConstructor(spinItems) : this.#spinTracksRandomizer(caseItem, 1),
    });
  }

  #setPrizeSpinTracksForReplay({ patchState, getState }: StateContext<AprilMadnessEventModel>, replayData: ICaseOpenPrizeDataDto): void {
    const { caseItems } = getState();
    const caseItemsArray = Object.values(caseItems);

    if (caseItemsArray.length === 0) {
      return;
    }

    const infinitySpin: ICaseSpinItem[][] = replayData.results.map((result, index) => {
      const trackItems: ICaseSpinItem[] = [];
      const prizeItem = result.caseRevisionItem;

      for (let i = 0; i < 32; i++) {
        if (i === 28) {
          trackItems.push({
            ...prizeItem,
            trackIndex: index,
            userInventoryItemId: result.userInventoryItemId,
            status: IItemStatus.TAKEN,
          });
        } else {
          const randomItem = caseItemsArray[Math.floor(Math.random() * caseItemsArray.length)];
          trackItems.push({ ...randomItem, trackIndex: index });
        }
      }

      return trackItems;
    });

    patchState({
      prizeSpinTracks: infinitySpin,
    });
  }

  #getSpinItemsAndSetPrizeSpinTracks(
    stateContext: StateContext<AprilMadnessEventModel>,
    caseId: number,
    caseItem: ICaseItemDtoV2,
  ): Observable<number[][]> {
    return this.#casesBackendService.getSpinItems(1, caseId).pipe(
      tap((spinItems: number[][]) => {
        this.#setPrizeSpinTracks(stateContext, caseItem, spinItems);
      }),
    );
  }

  #getUserQuestInfo(questId: number): Observable<ILevelQuestUserInfo | null> {
    return this.#aprilMadnessEventBackendService.getUserQuestInfo(questId).pipe(catchError(() => of(null)));
  }

  #onLastWonEvent({ getState, setState }: StateContext<AprilMadnessEventModel>, data: ILastDropData): void {
    const { topOfGamers } = getState();

    const newItem = motivatorItemAdapter(data);
    setState(
      patch({
        topOfGamers: compose(insertItem(newItem, 0), removeItem(topOfGamers?.length || 0)),
      }),
    );
  }

  #onLevelQuestUserUpdate({ getState, patchState }: StateContext<AprilMadnessEventModel>, data: ILevelQuestUserUpdate): void {
    const { nextLevelRequiredExp, eventLevels, userTakenItems } = getState();

    const currentLevelProgressPercent = getCurrentLevelProgressPercent(data.exp, nextLevelRequiredExp);
    const lastTakenLevelId = userTakenItems[userTakenItems.length - 1]?.levelId || 0;
    const nextLevelId = lastTakenLevelId + 1;

    const updatedEventLevels = eventLevels.map((level) => {
      if (level.levelId === nextLevelId && data.exp >= level.experience) {
        return { ...level, status: EAprilMadnessEventLevelStatus.AVAILABLE };
      }
      return level;
    });

    patchState({
      currentExp: data.exp,
      currentLevelProgressPercent,
      eventLevels: updatedEventLevels,
    });
  }

  #onLevelQuestUserLevelUpdate(
    { patchState, getState, dispatch }: StateContext<AprilMadnessEventModel>,
    data: ILevelQuestUserLevelUpdate,
  ): void {
    const { userTakenItems, collected, eventLevels, currentExp } = getState();

    patchState(levelQuestUserLevelUpdateAdapter(data, { userTakenItems, collected, eventLevels, currentExp }));
    dispatch([new GetPossibleItems()]);
  }

  #spinItemsMapper(caseItems: ICaseRevisionItem[]): Record<number, ICaseRevisionItem> {
    return (
      caseItems.reduce((acc: Record<number, ICaseRevisionItem>, item: ICaseRevisionItem) => {
        acc[item.inventoryItem.id] = item;
        return acc;
      }, {}) || {}
    );
  }

  #spinTracksRandomizer(item: ICaseItemDtoV2, lines: number): ICaseSpinItem[][] {
    const fillingItems: ICaseSpinItem[][] = [];
    for (let trackIndex = 0; trackIndex < lines; trackIndex++) {
      const lineItems: ICaseSpinItem[] = [];
      for (let itemIndex = 0; itemIndex < 32; itemIndex++) {
        const revItems = item.lastRevision?.items ?? [];
        const randomIndex = Math.floor(Math.random() * revItems.length);
        lineItems.push({ ...revItems[randomIndex], trackIndex: trackIndex });
      }
      fillingItems.push(lineItems);
    }
    return fillingItems;
  }
}
